import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/apiClient.js';
import { useApi, useMutation } from '../../lib/useApi.js';
import { humanizeEnum } from '../../lib/fieldLabels.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { format } from '@dha-nexus/shared';
import {
  DataSourceBadge,
  EmptyState,
  ErrorState,
  FormField,
  KpiHeroBand,
  InlineScoreCell,
  StatusDonutMini,
  LoadingSkeleton,
  NumberInput,
  PageHeader,
  PageViewerControls,
  Select,
  StatusChip,
  Textarea,
  TextInput,
  toast,
} from '../../components/index.js';
import { ErrorZone } from '../../components/primitives/ErrorZone.jsx';
import { CitationSourceLink } from '../../components/inspectors/AiCitationChip.jsx';
import { useClientPagination } from '../../lib/usePagination.js';
import './resources.css';
import './resources.redesign.css';

function parseRequiredSkills(value) {
  return String(value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [skillName, proficiency] = entry.split(':').map((part) => part.trim());
      return {
        skillName,
        proficiency: proficiency ? Number(proficiency) : 1,
      };
    })
    .filter((entry) => entry.skillName);
}

function parseAccessTags(value) {
  return String(value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function teamLabel(profile) {
  return profile.teamName || profile.teamId || 'Unassigned team';
}

function accessTagsLabel(record, { emptyLabel = 'No tags recorded' } = {}) {
  if (record?.accessTagsMasked) return 'Restricted';
  return (record?.accessTags || []).join(', ') || emptyLabel;
}

function formatInlineList(values = [], emptyLabel = 'None') {
  return Array.isArray(values) && values.length ? values.join(', ') : emptyLabel;
}

function recommendationRankingLabel(recommendation) {
  if (recommendation?.ranking?.label) return recommendation.ranking.label;
  const position = Number(recommendation?.ranking?.position || 0);
  const totalCandidates = Number(recommendation?.ranking?.totalCandidates || 0);
  if (position > 0 && totalCandidates > 0) return `Ranked #${position} of ${totalCandidates}`;
  return null;
}

// Utilization is healthiest near 100%: over-allocation (>100) is the worst
// signal, sustained underuse is a softer caution, and the band around target
// reads green. Mirrors the executive-traffic-light contract from the API
// status field when present, otherwise derives from the percentage.
function utilizationTone(percent, status) {
  const s = String(status || '').toLowerCase();
  if (s === 'overallocated') return 'brick';
  if (s === 'underutilized') return 'amber';
  if (s === 'target' || s === 'balanced' || s === 'on-target') return 'sage';
  const value = Number(percent);
  if (Number.isNaN(value)) return 'slate';
  if (value > 100) return 'brick';
  if (value >= 70) return 'sage';
  if (value >= 40) return 'amber';
  return 'slate';
}

// Roll utilization rows into an executive distribution for the rollup ring.
function buildUtilizationSegments(rows = []) {
  const counts = { brick: 0, sage: 0, amber: 0, slate: 0 };
  for (const row of rows) {
    counts[utilizationTone(row.utilizationPercent, row.status)] += 1;
  }
  return [
    { label: 'Over-allocated', count: counts.brick, tone: 'brick' },
    { label: 'On target', count: counts.sage, tone: 'sage' },
    { label: 'Underutilized', count: counts.amber, tone: 'amber' },
    { label: 'No reading', count: counts.slate, tone: 'slate' },
  ].filter((segment) => segment.count > 0);
}

function ResourceCard({ profile, utilizationRow }) {
  return (
    <article className="resource-card">
      <div className="row spread" style={{ gap: 12 }}>
        <div>
          <div className="eyebrow">{profile.role || profile.resourceType || 'Resource'}</div>
          <h4 style={{ margin: '4px 0 0' }}>{profile.displayName || profile.id}</h4>
        </div>
        <StatusChip status={utilizationRow?.status || profile.status || 'planned'} />
      </div>
      <p className="muted">
        {teamLabel(profile)} · target {Math.round(Number(profile.utilizationTarget || 0) * 100)}%
      </p>
      {utilizationRow && utilizationRow.utilizationPercent != null ? (
        <div className="resource-card__util">
          <div className="resource-card__util-caption">
            <span>Utilization</span>
            <span>{utilizationRow.utilizationPercent}%</span>
          </div>
          <InlineScoreCell
            value={utilizationRow.utilizationPercent}
            max={Math.max(125, Number(utilizationRow.utilizationPercent) || 0)}
            tone={utilizationTone(utilizationRow.utilizationPercent, utilizationRow.status)}
            suffix="%"
            ariaLabel={`Utilization ${utilizationRow.utilizationPercent}%`}
          />
        </div>
      ) : null}
      <div className="resource-card-footer">
        <span className="chip outline sm">
          {utilizationRow?.allocatedHours ?? 0}h / {utilizationRow?.availableHours ?? 0}h
        </span>
        {Array.isArray(profile.skills) && profile.skills.length ? (
          <span className="chip outline sm">{profile.skills.length} skills</span>
        ) : null}
      </div>
    </article>
  );
}

function UtilizationTable({ rows = [] }) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No utilization rows"
        description="Add resource profiles and weekly allocations to populate the scheduler."
      />
    );
  }

  return (
    <div className="table-scroll" data-testid="resource-table-scroll">
      <table className="table">
        <thead>
          <tr>
            <th>Resource</th>
            <th>Period</th>
            <th>Available</th>
            <th>Allocated</th>
            <th>Utilization</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.resourceId}>
              <td>{row.displayName || row.resourceId}</td>
              <td>{row.period || 'Current window'}</td>
              <td>{row.availableHours ?? 0}h</td>
              <td>{row.allocatedHours ?? 0}h</td>
              <td className="resource-util-col">
                <InlineScoreCell
                  value={row.utilizationPercent ?? 0}
                  max={Math.max(125, Number(row.utilizationPercent) || 0)}
                  tone={utilizationTone(row.utilizationPercent, row.status)}
                  suffix="%"
                  ariaLabel={`Utilization ${row.utilizationPercent ?? 0}%`}
                />
              </td>
              <td>
                <StatusChip status={row.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecommendationCard({ recommendation }) {
  return (
    <article className="resource-recommendation-card">
      <div className="row spread" style={{ gap: 12 }}>
        <div>
          <div className="eyebrow">{recommendation.fitStatus || 'qualified'}</div>
          <h4 style={{ margin: '4px 0 0' }}>
            {recommendation.displayName || recommendation.resourceId}
          </h4>
        </div>
        <span className="chip blue sm">Score {recommendation.score ?? 0}</span>
      </div>
      {recommendationRankingLabel(recommendation) ? (
        <div className="resource-card-footer">
          <span className="chip outline sm">{recommendationRankingLabel(recommendation)}</span>
        </div>
      ) : null}
      <div className="resource-recommendation-grid">
        <div>
          <dt>Skill fit</dt>
          <dd>{recommendation.skillFit?.summary || 'Skill fit not generated.'}</dd>
        </div>
        <div>
          <dt>Access fit</dt>
          <dd>{recommendation.accessFit?.summary || 'Access fit not generated.'}</dd>
        </div>
        <div>
          <dt>Availability / headroom</dt>
          <dd>{recommendation.availability?.summary || 'Availability posture not generated.'}</dd>
        </div>
        <div>
          <dt>Constraints</dt>
          <dd>{recommendation.constraints?.summary || 'No constraint narrative generated.'}</dd>
        </div>
      </div>
      <ul className="resource-rationale-list">
        {(recommendation.rationale || []).map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
      <div className="resource-card-footer">
        {recommendation.skillFit ? (
          <span className="chip outline sm">
            Matched skills {formatInlineList(recommendation.skillFit.matchedSkills)}
          </span>
        ) : null}
        {recommendation.accessFit ? (
          <span className="chip outline sm">
            Access gaps {formatInlineList(recommendation.accessFit.missingAccessTags)}
          </span>
        ) : null}
      </div>
    </article>
  );
}

// The allocation a "Resolve now" link should target for an over-allocated
// weekly cell: the largest active allocation contributing to the overage —
// the highest-leverage record to move first in the conflict flow (W-5).
function primaryConflictAllocationId(row) {
  const allocations = Array.isArray(row?.allocations) ? row.allocations : [];
  const active = allocations.filter(
    (allocation) => allocation?.id && allocation.status !== 'ended',
  );
  if (!active.length) return null;
  return active.reduce((best, allocation) =>
    Number(allocation.hoursPerWeek || 0) > Number(best.hoursPerWeek || 0) ? allocation : best,
  ).id;
}

// FS-RES-2 / AC-RES-2.1 — the merged capacity view's weekly grid:
// resources × weeks with utilization-toned cells; over-allocated cells carry
// a "Resolve now" link into the prefilled assignment wizard (conflict flow).
function CapacityGrid({ rows = [] }) {
  const periods = [...new Set(rows.map((row) => row.period || 'Current window'))].sort();
  const resourceRows = [];
  const byResource = new Map();
  for (const row of rows) {
    if (!byResource.has(row.resourceId)) {
      const entry = {
        resourceId: row.resourceId,
        displayName: row.displayName || row.resourceId,
        cells: new Map(),
      };
      byResource.set(row.resourceId, entry);
      resourceRows.push(entry);
    }
    byResource.get(row.resourceId).cells.set(row.period || 'Current window', row);
  }
  return (
    <div className="table-scroll" data-testid="resource-capacity-grid">
      <table className="table resource-capacity-table">
        <thead>
          <tr>
            <th scope="col">Resource</th>
            {periods.map((period) => (
              <th scope="col" key={period}>
                {period}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {resourceRows.map((entry) => (
            <tr key={entry.resourceId}>
              <th scope="row">{entry.displayName}</th>
              {periods.map((period) => {
                const cell = entry.cells.get(period);
                if (!cell) {
                  return (
                    <td key={period} className="resource-capacity-cell tone-slate">
                      <span className="muted small">No calendar</span>
                    </td>
                  );
                }
                const tone = utilizationTone(cell.utilizationPercent, cell.status);
                const conflictAllocationId =
                  String(cell.status || '').toLowerCase() === 'overallocated'
                    ? primaryConflictAllocationId(cell)
                    : null;
                return (
                  <td key={period} className={`resource-capacity-cell tone-${tone}`}>
                    <span className="resource-capacity-cell__pct">
                      {cell.utilizationPercent != null ? `${cell.utilizationPercent}%` : '—'}
                    </span>
                    <span className="resource-capacity-cell__hours muted small">
                      {cell.allocatedHours ?? 0}h / {cell.availableHours ?? 0}h
                    </span>
                    {conflictAllocationId ? (
                      <Link
                        className="resource-capacity-cell__resolve"
                        to={`/resources/assignments/new?allocationId=${conflictAllocationId}`}
                      >
                        Resolve now
                      </Link>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// AC-RES-2.1 — the legend defining exactly what each utilization tone means.
function CapacityLegend() {
  return (
    <ul className="resource-capacity-legend" aria-label="Utilization legend">
      <li>
        <span className="resource-util-bar__dot brick" aria-hidden />
        <strong>Over-allocated</strong>
        <span>
          {' '}
          — allocated hours exceed available hours (utilization above 100%). Resolve now opens the
          conflict flow.
        </span>
      </li>
      <li>
        <span className="resource-util-bar__dot sage" aria-hidden />
        <strong>On target</strong>
        <span> — utilization between 70% and 100% of available hours.</span>
      </li>
      <li>
        <span className="resource-util-bar__dot amber" aria-hidden />
        <strong>Underutilized</strong>
        <span> — utilization below 70%; headroom for approved intake.</span>
      </li>
      <li>
        <span className="resource-util-bar__dot slate" aria-hidden />
        <strong>No calendar</strong>
        <span> — no availability window or utilization reading recorded for the week.</span>
      </li>
    </ul>
  );
}

// The scheduling half of the merged capacity view (OT-04): allocation windows
// with real start/end dates, replacing the duplicated scheduler table.
function AllocationScheduleTable({ rows = [] }) {
  return (
    <div className="table-scroll" data-testid="resource-allocation-schedule">
      <table className="table">
        <thead>
          <tr>
            <th>Allocation target</th>
            <th>Resource</th>
            <th>LOE</th>
            <th>Hours</th>
            <th>Start</th>
            <th>End</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((allocation) => (
            <tr key={allocation.id}>
              <td>
                {allocation.targetRoute ? (
                  <Link to={allocation.targetRoute}>
                    {allocation.targetName || allocation.targetEntityId || allocation.id}
                  </Link>
                ) : (
                  allocation.targetName || allocation.targetEntityId || allocation.id
                )}
              </td>
              <td>{allocation.displayName || allocation.resourceId}</td>
              <td>{allocation.loeId || 'Unassigned'}</td>
              <td>{allocation.hoursPerWeek || 0}h/week</td>
              <td>{allocation.startDate || '—'}</td>
              <td>{allocation.endDate || '—'}</td>
              <td>
                <StatusChip status={allocation.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConflictStatusSummary({ conflict }) {
  if (!conflict) return null;
  const hasConflict = Boolean(conflict.hasConflict);
  return (
    <div className="resource-conflict-summary">
      <StatusChip status={hasConflict ? 'overallocated' : conflict.status || 'target'} />
      <span className="chip outline sm">
        {conflict.allocatedHours ?? 0}h allocated / {conflict.availableHours ?? 0}h available
      </span>
      {hasConflict ? (
        <span className="chip amber sm">{conflict.varianceHours ?? 0}h over capacity</span>
      ) : (
        <span className="chip green sm">Conflict cleared</span>
      )}
    </div>
  );
}

export function ResourceCommandCenterPage() {
  const [searchParams] = useSearchParams();
  const isCapacityView = searchParams.get('view') === 'capacity';
  usePageMetadata({
    title: isCapacityView ? 'Resource Capacity' : 'Resource Command Center',
    subtitle: isCapacityView
      ? 'Merged weekly capacity grid and allocation schedule across OWHA resource pools.'
      : 'Capacity conflicts, LOE staffing posture, and assignment pressure across OWHA work.',
  });
  const resources = useApi('/resources');
  const utilization = useApi('/resources/utilization');
  const allocations = useApi('/resource-allocations');
  const requests = useApi('/resource-requests', { query: { status: 'submitted' } });
  const profiles = resources.data?.data || [];
  const profileTotal = Number(resources.data?.page?.total ?? profiles.length);
  const utilizationRows = utilization.data?.data?.rows || [];
  const summary = utilization.data?.data?.summary || {};
  const allocationRows = allocations.data?.data || [];
  const allocationTotal = Number(allocations.data?.page?.total ?? allocationRows.length);
  const requestRows = requests.data?.data || [];
  const requestTotal = Number(requests.data?.page?.total ?? requestRows.length);
  const utilizationByResource = new Map(utilizationRows.map((row) => [row.resourceId, row]));
  const profilePager = useClientPagination(profiles, {
    initialPageSize: 6,
    resetKey: `resources-${profiles.length}`,
  });

  // Derive executive KPIs + rollups from data already fetched above.
  const utilizationSegments = buildUtilizationSegments(utilizationRows);
  const readingsWithPercent = utilizationRows.filter((row) => row.utilizationPercent != null);
  const avgUtilization = readingsWithPercent.length
    ? Math.round(
        readingsWithPercent.reduce((sum, row) => sum + Number(row.utilizationPercent || 0), 0) /
          readingsWithPercent.length,
      )
    : null;
  const overallocatedCount = summary.overallocatedCount || 0;
  const underutilizedCount = summary.underutilizedCount || 0;
  const missingCalendarCount = summary.missingCalendarCount || 0;
  const allocatedLoeEntries = Object.entries(summary.allocatedHoursByLoe || {});
  const maxLoeHours = allocatedLoeEntries.reduce(
    (max, [, hours]) => Math.max(max, Number(hours) || 0),
    0,
  );
  const kpiItems = [
    {
      id: 'headcount',
      label: 'Resources tracked',
      value: profileTotal,
      status: profileTotal > 0 ? 'blue' : 'slate',
      statusLabel: profileTotal > 0 ? 'Active roster' : 'Empty',
      footer: `${utilizationRows.length} with utilization readings`,
    },
    {
      id: 'avg-utilization',
      label: 'Average utilization',
      value: avgUtilization == null ? '—' : avgUtilization,
      unit: avgUtilization == null ? '' : '%',
      status: avgUtilization == null ? 'slate' : utilizationTone(avgUtilization),
      statusLabel:
        avgUtilization == null
          ? 'No readings'
          : avgUtilization > 100
            ? 'Over capacity'
            : avgUtilization >= 70
              ? 'On target'
              : 'Headroom',
      footer: 'Allocated vs available hours',
    },
    {
      id: 'overallocated',
      label: 'Over-allocated resources',
      value: overallocatedCount,
      status: overallocatedCount > 0 ? 'brick' : 'sage',
      statusLabel: overallocatedCount > 0 ? 'Resolve now' : 'Clear',
      footer: 'Immediate staffing conflicts',
    },
    {
      id: 'underutilized',
      label: 'Underutilized capacity',
      value: underutilizedCount,
      status: underutilizedCount > 0 ? 'amber' : 'sage',
      statusLabel: underutilizedCount > 0 ? 'Re-deploy' : 'None',
      footer:
        missingCalendarCount > 0
          ? `${missingCalendarCount} calendar gap${missingCalendarCount === 1 ? '' : 's'}`
          : 'Can absorb approved intake',
    },
  ];

  if (resources.loading || utilization.loading || allocations.loading || requests.loading) {
    return <LoadingSkeleton rows={6} />;
  }

  // FS-RES-2 / OT-04 — ?view=capacity renders the merged scheduler+utilization
  // capacity view: ONE weekly grid (no duplicated utilization table) plus the
  // real allocation schedule, with independent per-zone failure banners.
  if (isCapacityView) {
    return (
      <>
        <PageHeader
          eyebrow="OWHA Resources"
          title="Resource Capacity"
          subtitle="Weekly utilization grid and allocation scheduling merged into one capacity view."
          actions={
            <Link className="btn secondary sm" to="/resources">
              Back to command center
            </Link>
          }
        />

        {utilization.error ? (
          <ErrorZone
            title="capacity utilization"
            error={utilization.error}
            onRetry={utilization.refresh}
          />
        ) : (
          <>
            <KpiHeroBand
              eyebrow="Capacity rollup"
              asOf={utilization.loadedAt}
              className="resource-command__band"
              columns={4}
              items={kpiItems}
            />
            <section className="resource-panel">
              <div className="row spread" style={{ gap: 12 }}>
                <h3>Weekly capacity grid</h3>
                <span className="chip outline sm">
                  {utilizationRows.length} reading{utilizationRows.length === 1 ? '' : 's'}
                </span>
              </div>
              {utilizationRows.length === 0 ? (
                <EmptyState
                  title="No utilization readings"
                  description="Weekly capacity cells will render once resource calendars and allocations load."
                />
              ) : (
                <CapacityGrid rows={utilizationRows} />
              )}
              <CapacityLegend />
            </section>
          </>
        )}

        <section className="resource-panel">
          <div className="row spread" style={{ gap: 12 }}>
            <h3>Allocation schedule</h3>
            <span className="chip outline sm">{allocationTotal}</span>
          </div>
          {allocations.error ? (
            <ErrorZone
              title="the allocation schedule"
              error={allocations.error}
              onRetry={allocations.refresh}
            />
          ) : allocationRows.length === 0 ? (
            <EmptyState
              title="No allocation windows"
              description="Active allocations with start and end dates will schedule here."
            />
          ) : (
            <AllocationScheduleTable rows={allocationRows} />
          )}
        </section>
      </>
    );
  }

  // AC-STATE-1: any one of the four fetches failing banners its own zone below
  // while every healthy zone keeps rendering — never a blank command center.
  const postureError = resources.error || utilization.error;

  return (
    <>
      <PageHeader
        eyebrow="OWHA Resources"
        title="Resource Command Center"
        subtitle="Mission-ready staffing visibility for LOE-aligned delivery and conflict resolution."
        actions={
          <Link className="btn primary sm" to="/resources/assignments/new">
            Open assignment wizard
          </Link>
        }
      />

      {postureError ? (
        <ErrorZone
          title="staffing posture"
          error={postureError}
          onRetry={resources.error ? resources.refresh : utilization.refresh}
        />
      ) : (
        <KpiHeroBand
          eyebrow="OWHA staffing posture"
          asOf={resources.loadedAt}
          className="resource-command__band"
          columns={4}
          items={kpiItems}
        />
      )}

      {utilization.error ? null : utilizationRows.length || allocatedLoeEntries.length ? (
        <section className="resource-mission-row" aria-label="Mission staffing overview">
          <article className="resource-mission-card">
            <div className="resource-mission-card__head">
              <div>
                <div className="eyebrow">Utilization distribution</div>
                <h3>Capacity mix</h3>
              </div>
              <span className="chip outline sm">
                {utilizationRows.length} reading{utilizationRows.length === 1 ? '' : 's'}
              </span>
            </div>
            {utilizationSegments.length ? (
              <div className="resource-donut-wrap">
                <StatusDonutMini
                  segments={utilizationSegments}
                  size={132}
                  totalLabel="Resources"
                  ariaLabel="Utilization distribution"
                />
              </div>
            ) : (
              <p className="muted">No utilization readings are available for the current window.</p>
            )}
          </article>

          <article className="resource-mission-card">
            <div className="resource-mission-card__head">
              <div>
                <div className="eyebrow">LOE allocation</div>
                <h3>Hours by line of effort</h3>
              </div>
              <Link to="/loe-dashboard">LOE dashboard</Link>
            </div>
            {allocatedLoeEntries.length ? (
              <div className="resource-util-bars">
                {allocatedLoeEntries.map(([loeId, hours]) => (
                  <div key={loeId} className="resource-util-bar">
                    <div className="resource-util-bar__label">
                      <span className="resource-util-bar__dot blue" aria-hidden />
                      <span className="resource-util-bar__name">{loeId}</span>
                    </div>
                    <span className="resource-util-bar__count">{hours}h</span>
                    <div className="resource-loe-bar" style={{ gridColumn: '1 / -1' }}>
                      <div className="resource-loe-bar__track">
                        <div
                          className="resource-loe-bar__fill"
                          style={{
                            width: `${maxLoeHours ? Math.max(6, (Number(hours) / maxLoeHours) * 100) : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="muted">Approved allocations will roll up here by Director LOE.</p>
            )}
          </article>
        </section>
      ) : null}

      <section className="resource-shell-grid">
        <div className="resource-panel">
          <div className="row spread" style={{ gap: 12 }}>
            <h3>Resource roster</h3>
            <span className="chip outline sm">{profileTotal}</span>
          </div>
          {resources.error ? (
            <ErrorZone
              title="the resource roster"
              error={resources.error}
              onRetry={resources.refresh}
            />
          ) : profiles.length === 0 ? (
            <EmptyState
              title="No resources found"
              description="Seed or ingest resource profiles to activate the command center."
            />
          ) : (
            <>
              <div className="resource-card-grid">
                {profilePager.items.map((profile) => (
                  <ResourceCard
                    key={profile.id}
                    profile={profile}
                    utilizationRow={utilizationByResource.get(profile.id)}
                  />
                ))}
              </div>
              <PageViewerControls
                page={profilePager.page}
                label="resource profiles"
                pageSize={profilePager.pageSize}
                onOffsetChange={profilePager.setOffset}
                onPageSizeChange={profilePager.setPageSize}
              />
            </>
          )}
        </div>

        <div className="resource-panel">
          <div className="row spread" style={{ gap: 12 }}>
            <h3>Pending staffing requests</h3>
            <span className="chip outline sm">{requestTotal}</span>
          </div>
          {requests.error ? (
            <ErrorZone
              title="pending staffing requests"
              error={requests.error}
              onRetry={requests.refresh}
            />
          ) : requestRows.length === 0 ? (
            <EmptyState
              title="No queued staffing requests"
              description="Project managers can submit role and skill demand from project resource tabs."
            />
          ) : (
            <div className="resource-stack">
              {requestRows.map((request) => (
                <article key={request.id} className="resource-row-card">
                  <div className="row spread" style={{ gap: 12 }}>
                    <strong>{request.title || request.roleName || request.id}</strong>
                    <StatusChip status={request.status || 'submitted'} />
                  </div>
                  <p className="muted">
                    {request.projectName || request.projectId} ·{' '}
                    {request.roleName || 'Role not specified'}
                  </p>
                  <p className="muted">
                    {request.startDate || 'Start TBD'} to {request.endDate || 'End TBD'} ·{' '}
                    {request.hoursPerWeek || 0}h/week
                  </p>
                  <div className="resource-card-footer">
                    {request.projectRoute ? (
                      <Link to={request.projectRoute}>Open project</Link>
                    ) : null}
                    {request.assignmentRoute ? (
                      <Link to={request.assignmentRoute}>Open assignment wizard</Link>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        <div className="resource-panel">
          <div className="row spread" style={{ gap: 12 }}>
            <h3>LOE allocation posture</h3>
            <Link to="/loe-dashboard">Open LOE dashboard</Link>
          </div>
          {utilization.error ? (
            <ErrorZone
              title="LOE allocation posture"
              error={utilization.error}
              onRetry={utilization.refresh}
            />
          ) : Object.keys(summary.allocatedHoursByLoe || {}).length === 0 ? (
            <EmptyState
              title="No LOE allocations"
              description="Approved allocations will roll up here by Director LOE."
            />
          ) : (
            <div className="resource-stack">
              {Object.entries(summary.allocatedHoursByLoe || {}).map(([loeId, hours]) => (
                <article key={loeId} className="resource-row-card">
                  <div className="row spread" style={{ gap: 12 }}>
                    <strong>{loeId}</strong>
                    <span className="chip outline sm">{hours} hours</span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        <div className="resource-panel">
          <div className="row spread" style={{ gap: 12 }}>
            <h3>Active allocations</h3>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <span className="chip outline sm">{allocationTotal}</span>
              <Link to="/resources?view=capacity">Open capacity view</Link>
            </div>
          </div>
          {allocations.error ? (
            <ErrorZone
              title="active allocations"
              error={allocations.error}
              onRetry={allocations.refresh}
            />
          ) : allocationRows.length === 0 ? (
            <EmptyState
              title="No active allocations"
              description="Assignments will appear here once resource planning begins."
            />
          ) : (
            <div className="resource-stack">
              {allocationRows.map((allocation) => (
                <article key={allocation.id} className="resource-row-card">
                  <div className="row spread" style={{ gap: 12 }}>
                    <strong>{allocation.targetName || allocation.id}</strong>
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      {utilizationByResource.get(allocation.resourceId)?.status ===
                      'overallocated' ? (
                        <Link
                          className="btn secondary sm"
                          to={`/resources/allocations/${allocation.id}`}
                        >
                          Open conflict detail
                        </Link>
                      ) : null}
                      <StatusChip status={allocation.status} />
                    </div>
                  </div>
                  <p className="muted">
                    {allocation.displayName || allocation.resourceId} ·{' '}
                    {allocation.hoursPerWeek || 0}h/week
                  </p>
                  <p className="muted">
                    {allocation.loeId || 'Unassigned LOE'}
                    {allocation.targetRoute ? (
                      <>
                        {' '}
                        · <Link to={allocation.targetRoute}>Open project</Link>
                      </>
                    ) : null}
                  </p>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export function ResourceSchedulerPage() {
  usePageMetadata({
    title: 'Resource Scheduler',
    subtitle: 'Weekly availability, over-allocation, and handoff posture for resource planning.',
  });
  const utilization = useApi('/resources/utilization');

  if (utilization.loading) return <LoadingSkeleton rows={6} />;
  if (utilization.error) return <ErrorState error={utilization.error} />;

  const rows = utilization.data?.data?.rows || [];

  return (
    <>
      <PageHeader
        eyebrow="OWHA Resources"
        title="Resource Scheduler"
        subtitle="Weekly staffing windows for active project demand, pilot support, and sustainment overlap."
      />
      <section className="resource-panel">
        <UtilizationTable rows={rows} />
      </section>
    </>
  );
}

export function ResourceUtilizationPage() {
  usePageMetadata({
    title: 'Utilization Report',
    subtitle: 'Resource headroom and over-allocation rollups across OWHA delivery.',
  });
  const utilization = useApi('/resources/utilization');

  if (utilization.loading) return <LoadingSkeleton rows={6} />;
  if (utilization.error) return <ErrorState error={utilization.error} />;

  const rows = utilization.data?.data?.rows || [];
  const summary = utilization.data?.data?.summary || {};
  const overallocatedCount = summary.overallocatedCount || 0;
  const underutilizedCount = summary.underutilizedCount || 0;
  const missingCalendarCount = summary.missingCalendarCount || 0;
  const utilizationSegments = buildUtilizationSegments(rows);
  const kpiItems = [
    {
      id: 'overallocated',
      label: 'Over-allocated',
      value: overallocatedCount,
      status: overallocatedCount > 0 ? 'brick' : 'sage',
      statusLabel: overallocatedCount > 0 ? 'Resolve now' : 'Clear',
      footer: 'Above available capacity',
    },
    {
      id: 'underutilized',
      label: 'Underutilized',
      value: underutilizedCount,
      status: underutilizedCount > 0 ? 'amber' : 'sage',
      statusLabel: underutilizedCount > 0 ? 'Re-deploy' : 'None',
      footer: 'Headroom for approved intake',
    },
    {
      id: 'missing-calendars',
      label: 'Missing calendars',
      value: missingCalendarCount,
      status: missingCalendarCount > 0 ? 'slate' : 'sage',
      statusLabel: missingCalendarCount > 0 ? 'Backfill' : 'Complete',
      footer: 'No availability window on file',
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="OWHA Resources"
        title="Utilization Report"
        subtitle="Headroom, underutilization, and over-commitment across resource pools."
      />
      <KpiHeroBand
        eyebrow="Utilization rollup"
        asOf={utilization.loadedAt}
        className="resource-command__band"
        columns={3}
        items={kpiItems}
      />
      {utilizationSegments.length ? (
        <section className="resource-mission-row" aria-label="Utilization overview">
          <article className="resource-mission-card">
            <div className="resource-mission-card__head">
              <div>
                <div className="eyebrow">Capacity mix</div>
                <h3>Utilization distribution</h3>
              </div>
              <span className="chip outline sm">
                {rows.length} reading{rows.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="resource-donut-wrap">
              <StatusDonutMini
                segments={utilizationSegments}
                size={132}
                totalLabel="Resources"
                ariaLabel="Utilization distribution"
              />
            </div>
          </article>
          <article className="resource-mission-card">
            <div className="resource-mission-card__head">
              <div>
                <div className="eyebrow">Band breakdown</div>
                <h3>Where capacity sits</h3>
              </div>
            </div>
            <div className="resource-util-bars">
              {utilizationSegments.map((segment) => (
                <div key={segment.label} className="resource-util-bar">
                  <div className="resource-util-bar__label">
                    <span className={`resource-util-bar__dot ${segment.tone}`} aria-hidden />
                    <span className="resource-util-bar__name">{segment.label}</span>
                  </div>
                  <span className="resource-util-bar__count">{segment.count}</span>
                </div>
              ))}
            </div>
          </article>
        </section>
      ) : null}
      <section className="resource-panel">
        <UtilizationTable rows={rows} />
      </section>
    </>
  );
}

export function ResourceSkillsMatrixPage() {
  usePageMetadata({
    title: 'Skills Matrix',
    subtitle: 'Verified capability, proficiency, and access posture across OWHA resource pools.',
  });
  const resources = useApi('/resources');

  if (resources.loading) return <LoadingSkeleton rows={6} />;
  if (resources.error) return <ErrorState error={resources.error} />;

  const profiles = resources.data?.data || [];

  return (
    <>
      <PageHeader
        eyebrow="OWHA Resources"
        title="Skills Matrix"
        subtitle="Validated skills and access tags used by the assignment wizard."
      />
      <section className="resource-panel">
        {profiles.length === 0 ? (
          <EmptyState
            title="No resource skills"
            description="Verified skills will appear here once profiles are loaded."
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Resource</th>
                <th>Role</th>
                <th>Team</th>
                <th>Skills</th>
                <th>Access</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((profile) => (
                <tr key={profile.id}>
                  <td>{profile.displayName || profile.id}</td>
                  <td>{profile.role || '—'}</td>
                  <td>{teamLabel(profile)}</td>
                  <td>
                    {(profile.skills || []).map((skill) => skill.skillName).join(', ') || '—'}
                  </td>
                  <td>{accessTagsLabel(profile, { emptyLabel: '—' })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

export function ResourceConflictDetailPage() {
  const { id } = useParams();
  usePageMetadata({
    title: 'Allocation Conflict Detail',
    subtitle: 'Inspect impacted projects and work items before reassignment.',
  });
  const detail = useApi(`/resource-allocations/${id}/conflict`, { skip: !id });

  const conflictDetail = detail.data?.data || {};
  const allocation = conflictDetail.allocation || null;
  const relatedProjects = conflictDetail.relatedProjects || [];
  // AC-W3 / GAP A — the allocation-conflict page must show the affected project's
  // governed budget posture. Resolve the primary impacted project (the allocation's
  // own project first, else the first related project) and fetch it from the same
  // /projects/:id endpoint the Money tab reads, so the budget figure and source
  // badge are identical by construction. The hook runs on every render (skip when
  // there is no project) to keep hook order stable across loading states.
  const primaryProjectId =
    (allocation?.targetEntityType === 'project' && allocation?.targetEntityId) ||
    relatedProjects[0]?.id ||
    null;
  const projectDetail = useApi(primaryProjectId ? `/projects/${primaryProjectId}` : '', {
    skip: !primaryProjectId,
  });

  if (detail.loading) return <LoadingSkeleton rows={6} />;
  if (detail.error) return <ErrorState error={detail.error} />;

  const resourceProfile = conflictDetail.resourceProfile || null;
  const conflict = conflictDetail.conflict || null;
  const relatedWorkItems = conflictDetail.relatedWorkItems || [];

  // Single-entity envelopes return { data: <project> }; the shared empty-fetch
  // fallback returns { data: [] }. Treat an array (or missing) payload as "no
  // governed project" so we render honest absence instead of a fabricated figure.
  const projectRecord =
    projectDetail.data?.data && !Array.isArray(projectDetail.data.data)
      ? projectDetail.data.data
      : null;
  const projectFinancialSummary = projectRecord?.financialSummary || null;
  const plannedBudgetUsdM = Number(projectFinancialSummary?.plannedBudgetUsdM || 0);
  const actualSpendUsdM = Number(projectFinancialSummary?.actualSpendUsdM || 0);
  const hasGovernedBudget =
    Boolean(projectFinancialSummary) && projectFinancialSummary.plannedBudgetUsdM != null;

  return (
    <>
      <PageHeader
        eyebrow="OWHA Resources"
        title="Allocation conflict detail"
        subtitle="Inspect impacted mission work before moving staffed capacity."
        actions={
          allocation?.id ? (
            <Link
              className="btn primary sm"
              to={`/resources/assignments/new?allocationId=${allocation.id}`}
            >
              Find replacement
            </Link>
          ) : null
        }
      />

      <section className="resource-shell-grid">
        <div className="resource-panel">
          <div className="row spread" style={{ gap: 12 }}>
            <div>
              <div className="eyebrow">Current assignment</div>
              <h3 style={{ margin: '4px 0 0' }}>
                {resourceProfile?.displayName || allocation?.resourceId || id}
              </h3>
            </div>
            <ConflictStatusSummary conflict={conflict} />
          </div>
          <div className="resource-detail-grid">
            <article className="resource-row-card">
              <div className="eyebrow">Allocation</div>
              <strong>{allocation?.id || 'Unknown allocation'}</strong>
              <p className="muted">
                {resourceProfile?.role || 'Resource'} · {allocation?.hoursPerWeek || 0}h/week
              </p>
              <p>{allocation?.loeId || 'Unassigned LOE'}</p>
            </article>
            <article className="resource-row-card">
              <div className="eyebrow">Resource posture</div>
              <strong>{resourceProfile?.teamName || teamLabel(resourceProfile || {})}</strong>
              <p className="muted">Access: {accessTagsLabel(resourceProfile)}</p>
              <p>
                Skills:{' '}
                {(resourceProfile?.skills || []).map((skill) => skill.skillName).join(', ') ||
                  'No skills recorded'}
              </p>
            </article>
          </div>
        </div>

        <div className="resource-panel">
          <div className="row spread" style={{ gap: 12 }}>
            <h3>Governed budget posture</h3>
            {hasGovernedBudget ? (
              <DataSourceBadge
                source={
                  projectFinancialSummary.sourceSystem ||
                  projectFinancialSummary.dataTrust?.sourceSystem ||
                  'PMRT'
                }
                freshness={projectFinancialSummary.dataTrust?.freshness}
                confidence={projectFinancialSummary.dataTrust?.confidence}
                lineageTargetId={projectFinancialSummary.representativeFinancialRecordId || null}
              />
            ) : null}
          </div>
          {projectDetail.loading ? (
            <LoadingSkeleton rows={2} />
          ) : hasGovernedBudget ? (
            <div className="resource-detail-grid">
              <article className="resource-row-card">
                <div className="eyebrow">Planned</div>
                <strong>{format.formatCurrencyMillions(plannedBudgetUsdM * 1_000_000)}</strong>
                <p className="muted">
                  {projectRecord?.name || primaryProjectId} governed budget baseline
                </p>
              </article>
              <article className="resource-row-card">
                <div className="eyebrow">Expended</div>
                <strong>{format.formatCurrencyMillions(actualSpendUsdM * 1_000_000)}</strong>
                <p className="muted">Governed actual spend before this reassignment</p>
              </article>
            </div>
          ) : (
            <EmptyState
              title="No governed budget data"
              description="This allocation's project has no governed financial summary to cross-check yet."
            />
          )}
        </div>

        <div className="resource-panel">
          <div className="row spread" style={{ gap: 12 }}>
            <h3>Impacted projects</h3>
            <span className="chip outline sm">{relatedProjects.length}</span>
          </div>
          {relatedProjects.length === 0 ? (
            <EmptyState
              title="No impacted projects"
              description="Project context will appear here when the allocation is tied to active work."
            />
          ) : (
            <div className="resource-stack">
              {relatedProjects.map((project) => (
                <article key={project.id} className="resource-row-card">
                  <div className="row spread" style={{ gap: 12 }}>
                    <strong>{project.name || project.id}</strong>
                    <StatusChip status={project.status || 'planned'} />
                  </div>
                  <p className="muted">{project.lifecycleStage || 'Lifecycle stage unavailable'}</p>
                  {project.route ? <Link to={project.route}>Open project</Link> : null}
                </article>
              ))}
            </div>
          )}
        </div>

        <div className="resource-panel">
          <div className="row spread" style={{ gap: 12 }}>
            <h3>Impacted work items</h3>
            <span className="chip outline sm">{relatedWorkItems.length}</span>
          </div>
          {relatedWorkItems.length === 0 ? (
            <EmptyState
              title="No work items found"
              description="Open work items tied to impacted projects will appear here for review."
            />
          ) : (
            <div className="resource-stack">
              {relatedWorkItems.map((workItem) => (
                <article key={workItem.id} className="resource-row-card">
                  <div className="row spread" style={{ gap: 12 }}>
                    <strong>{workItem.title || workItem.id}</strong>
                    <StatusChip status={workItem.status || 'planned'} />
                  </div>
                  <p className="muted">
                    {workItem.type || 'task'} · {workItem.projectId || 'No project'}
                  </p>
                  {workItem.route ? <Link to={workItem.route}>Open work item</Link> : null}
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

const WIZARD_STEPS = [
  { id: 1, label: 'Demand' },
  { id: 2, label: 'Candidates' },
  { id: 3, label: 'Confirm' },
];

const PROFICIENCY_OPTIONS = [
  { value: '1', label: '1 — Aware' },
  { value: '2', label: '2 — Practicing' },
  { value: '3', label: '3 — Proficient' },
  { value: '4', label: '4 — Advanced' },
  { value: '5', label: '5 — Expert' },
];

function mapDemandSkills(skills = []) {
  return (Array.isArray(skills) ? skills : [])
    .filter((skill) => skill && skill.skillName)
    .map((skill) => ({ skillName: skill.skillName, proficiency: Number(skill.proficiency) || 1 }));
}

// FS-RES-5 / AC-RES-5.1 / AC-RES-5.2 — true 3-step assignment wizard:
//   1 Demand    — structured pickers (staffing request, LOE, hours, skill rows,
//                 access-tag checkboxes). No comma micro-syntax anywhere.
//   2 Candidates— governed AI recommendations side-by-side with real payload
//                 fields only (fit, displayed confidence, matched skills,
//                 access gaps, run id, review state, navigable citations).
//   3 Confirm   — Resource-Manager-gated apply with an editable audit note;
//                 other roles see a held, explained approval state.
// Post-apply the wizard navigates to the resolved allocation (W-5).
export function AssignmentWizardPage() {
  const { roles } = useCurrentUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const allocationId = searchParams.get('allocationId') || '';
  const requestIdParam = searchParams.get('requestId') || '';
  usePageMetadata({
    title: 'Assignment Wizard',
    subtitle:
      'Recommend resources for approved demand with skill, access, and capacity constraints.',
  });
  const findMut = useMutation();
  const applyMut = useMutation();
  const conflictDetail = useApi(`/resource-allocations/${allocationId}/conflict`, {
    skip: !allocationId,
  });
  const staffingRequestDetail = useApi(`/resource-requests/${requestIdParam}`, {
    skip: !requestIdParam,
  });
  const requestsList = useApi('/resource-requests', {
    query: { status: 'submitted' },
    skip: Boolean(allocationId),
  });
  const roster = useApi('/resources');

  const [step, setStep] = useState(1);
  const [requestId, setRequestId] = useState(requestIdParam);
  const [loeId, setLoeId] = useState('');
  const [hoursPerWeek, setHoursPerWeek] = useState(null);
  const [skills, setSkills] = useState([]);
  const [accessTags, setAccessTags] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationMeta, setRecommendationMeta] = useState(null);
  const [recommendationCitations, setRecommendationCitations] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [auditNote, setAuditNote] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const canApproveAssignments = Array.isArray(roles) && roles.includes('ROLE-RESOURCE-MANAGER');

  const conflictData = conflictDetail.data?.data || {};
  const recommendationRequest = conflictData.recommendationRequest || null;
  const currentResourceId = conflictData.allocation?.resourceId || '';
  const requestRows = Array.isArray(requestsList.data?.data) ? requestsList.data.data : [];
  const requestDetailData =
    staffingRequestDetail.data?.data && !Array.isArray(staffingRequestDetail.data.data)
      ? staffingRequestDetail.data.data
      : null;
  const rosterProfiles = Array.isArray(roster.data?.data) ? roster.data.data : [];

  // The staffing request currently sourcing the demand — from the structured
  // picker (or the ?requestId deep link), resolved against the open-request
  // list first, then the request detail fetch.
  const activeRequest = useMemo(() => {
    if (!requestId) return null;
    return (
      requestRows.find((row) => row.id === requestId) ||
      (requestDetailData?.id === requestId ? requestDetailData : null)
    );
  }, [requestId, requestRows, requestDetailData]);

  // Prefill demand exactly once per source record (request pick or conflict).
  const appliedDemandSourceRef = useRef('');

  useEffect(() => {
    if (!activeRequest || appliedDemandSourceRef.current === activeRequest.id) return;
    appliedDemandSourceRef.current = activeRequest.id;
    setLoeId(activeRequest.loeId || '');
    setHoursPerWeek(activeRequest.hoursPerWeek ?? null);
    setSkills(mapDemandSkills(activeRequest.requiredSkills));
    setAccessTags([...(activeRequest.accessTags || [])]);
  }, [activeRequest]);

  useEffect(() => {
    if (
      !recommendationRequest ||
      appliedDemandSourceRef.current === recommendationRequest.requestId
    )
      return;
    appliedDemandSourceRef.current = recommendationRequest.requestId;
    setRequestId(recommendationRequest.requestId || '');
    setLoeId(recommendationRequest.loeId || '');
    setHoursPerWeek(recommendationRequest.requiredHoursPerWeek ?? null);
    setSkills(mapDemandSkills(recommendationRequest.requiredSkills));
    setAccessTags([...(recommendationRequest.accessTags || [])]);
  }, [recommendationRequest]);

  const skillOptions = useMemo(() => {
    const names = new Set();
    for (const profile of rosterProfiles) {
      for (const skill of profile.skills || []) {
        if (skill?.skillName) names.add(skill.skillName);
      }
    }
    for (const skill of skills) {
      if (skill.skillName) names.add(skill.skillName);
    }
    return [...names].sort();
  }, [rosterProfiles, skills]);

  const accessTagOptions = useMemo(() => {
    const tags = new Set();
    for (const profile of rosterProfiles) {
      for (const tag of profile.accessTags || []) tags.add(tag);
    }
    for (const tag of accessTags) tags.add(tag);
    return [...tags].sort();
  }, [rosterProfiles, accessTags]);

  const loeOptions = useMemo(() => {
    const options = new Set(['LOE 1', 'LOE 2', 'LOE 3', 'LOE 4', 'LOE 5']);
    if (loeId) options.add(loeId);
    for (const row of requestRows) {
      if (row.loeId) options.add(row.loeId);
    }
    return [...options].sort();
  }, [loeId, requestRows]);

  const requestOptions = requestRows.map((row) => ({
    value: row.id,
    label: `${row.title || row.roleName || row.id} (${row.id})`,
  }));

  function updateSkill(index, patch) {
    setSkills((current) =>
      current.map((skill, i) => (i === index ? { ...skill, ...patch } : skill)),
    );
  }

  function addSkill() {
    setSkills((current) => [...current, { skillName: '', proficiency: 3 }]);
  }

  function removeSkill(index) {
    setSkills((current) => current.filter((_, i) => i !== index));
  }

  function toggleAccessTag(tag) {
    setAccessTags((current) =>
      current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag],
    );
  }

  async function findCandidates(event) {
    event.preventDefault();
    setErrorMessage('');
    try {
      const response = await findMut.run(() =>
        api.post('/ai/assignment-recommendation', {
          requestId,
          loeId,
          requiredHoursPerWeek: Number(hoursPerWeek || 0),
          requiredSkills: mapDemandSkills(skills),
          accessTags: [...accessTags],
        }),
      );
      const payload = response?.data?.data || response?.data || response;
      setRecommendations(payload?.recommendations || []);
      setRecommendationCitations(payload?.citations || []);
      setRecommendationMeta({
        reviewState: payload?.reviewState || null,
        runId: payload?.runId || null,
        citationCount: payload?.citationIds?.length || payload?.citations?.length || 0,
        confidence: payload?.confidence ?? null,
        approvalSummary: payload?.approval?.summary || null,
        approvalRequired: Boolean(payload?.approval?.required),
      });
      setSelectedCandidate(null);
      setStep(2);
    } catch (error) {
      setErrorMessage(error.message || 'Recommendation request failed.');
    }
  }

  function selectCandidate(recommendation) {
    setSelectedCandidate(recommendation);
    setAuditNote(
      `Assigning ${recommendation.displayName || recommendation.resourceId} via governed recommendation ${recommendation.id}${
        recommendationMeta?.runId ? ` (run ${recommendationMeta.runId})` : ''
      }.`,
    );
    setErrorMessage('');
    setStep(3);
  }

  async function applyAssignment() {
    if (!selectedCandidate) return;
    setErrorMessage('');
    try {
      if (allocationId) {
        const response = await applyMut.run(() =>
          api.post(`/resource-allocations/${allocationId}/reassign`, {
            newResourceId: selectedCandidate.resourceId,
            note: auditNote,
          }),
        );
        const allocation = response?.data?.data?.allocation || response?.data?.allocation || null;
        toast.success({
          title: 'Reassignment applied',
          body: `Allocation moved to ${selectedCandidate.displayName || selectedCandidate.resourceId}. Conflict posture refreshed.`,
        });
        navigate(`/resources/allocations/${allocation?.id || allocationId}`);
      } else {
        const response = await applyMut.run(() =>
          api.post(`/resource-requests/${requestId}/assign`, {
            resourceId: selectedCandidate.resourceId,
            note: auditNote,
          }),
        );
        const payload = response?.data?.data || response?.data || {};
        const allocation = payload.allocation || null;
        toast.success({
          title: 'Allocation created',
          body: `Staffing request assigned to ${selectedCandidate.displayName || selectedCandidate.resourceId}. Project manager notified.`,
        });
        if (allocation?.id) navigate(`/resources/allocations/${allocation.id}`);
        else navigate('/resources');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Assignment failed.');
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="OWHA Resources"
        title="Assignment Wizard"
        subtitle="Find the best-fit resource based on skill, access, and weekly headroom."
      />

      <ol className="resource-wizard-steps" aria-label="Assignment wizard steps">
        {WIZARD_STEPS.map((wizardStep) => (
          <li
            key={wizardStep.id}
            className={`resource-wizard-step${step === wizardStep.id ? ' is-active' : ''}${
              step > wizardStep.id ? ' is-complete' : ''
            }`}
            aria-current={step === wizardStep.id ? 'step' : undefined}
          >
            <span className="resource-wizard-step__index" aria-hidden>
              {step > wizardStep.id ? '✓' : wizardStep.id}
            </span>
            <span className="resource-wizard-step__label">{wizardStep.label}</span>
          </li>
        ))}
      </ol>

      <section className="resource-shell-grid">
        {activeRequest ? (
          <div className="resource-panel">
            <div className="row spread" style={{ gap: 12 }}>
              <div>
                <div className="eyebrow">Staffing request context</div>
                <h3 style={{ margin: '4px 0 0' }}>{activeRequest.title || activeRequest.id}</h3>
              </div>
              <StatusChip status={activeRequest.status || 'submitted'} />
            </div>
            <p className="muted">
              {activeRequest.projectName || activeRequest.projectId} ·{' '}
              {activeRequest.roleName || 'Role not specified'}
            </p>
            <p className="muted">
              {activeRequest.startDate || 'Start TBD'} to {activeRequest.endDate || 'End TBD'} ·{' '}
              {activeRequest.hoursPerWeek || 0}
              h/week
            </p>
            {activeRequest.justification ? <p>{activeRequest.justification}</p> : null}
          </div>
        ) : null}

        {allocationId ? (
          <div className="resource-panel">
            <div className="row spread" style={{ gap: 12 }}>
              <div>
                <div className="eyebrow">Conflict resolution context</div>
                <h3 style={{ margin: '4px 0 0' }}>
                  {conflictData.resourceProfile?.displayName ||
                    conflictData.allocation?.resourceId ||
                    allocationId}
                </h3>
              </div>
              <ConflictStatusSummary conflict={conflictData.conflict} />
            </div>
            <p className="muted">
              {conflictData.allocation?.targetEntityId || 'No target'} ·{' '}
              {conflictData.allocation?.hoursPerWeek || 0}h/week ·{' '}
              {conflictData.allocation?.loeId || 'Unassigned LOE'}
            </p>
            {conflictData.relatedProjects?.length ? (
              <div className="resource-card-footer">
                {conflictData.relatedProjects.map((project) => (
                  <span key={project.id} className="chip outline sm">
                    {project.name || project.id}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {step === 1 ? (
          <form className="resource-panel resource-form" onSubmit={findCandidates}>
            <div className="row spread" style={{ gap: 12 }}>
              <h3>Demand</h3>
              <span className="chip outline sm">Step 1 of 3</span>
            </div>

            {!allocationId ? (
              <FormField
                label="Staffing request"
                htmlFor="wizard-staffing-request"
                hint="Pick the approved staffing request sourcing this demand."
              >
                <Select
                  id="wizard-staffing-request"
                  value={requestId}
                  onChange={setRequestId}
                  options={requestOptions}
                  placeholder="Select a staffing request"
                />
              </FormField>
            ) : null}
            <FormField label="LOE" htmlFor="wizard-loe">
              <Select
                id="wizard-loe"
                value={loeId}
                onChange={setLoeId}
                options={loeOptions}
                placeholder="Select line of effort"
              />
            </FormField>
            <FormField label="Hours per week" htmlFor="wizard-hours">
              <NumberInput
                id="wizard-hours"
                value={hoursPerWeek}
                onChange={setHoursPerWeek}
                min={1}
                step={1}
              />
            </FormField>

            <fieldset className="resource-wizard-fieldset">
              <legend>Required skills</legend>
              {skills.length === 0 ? (
                <p className="muted small">No skill demand yet — add the first required skill.</p>
              ) : null}
              {skills.map((skill, index) => (
                <div className="resource-wizard-skill-row" key={index}>
                  <FormField label="Skill" htmlFor={`wizard-skill-${index}`}>
                    <Select
                      id={`wizard-skill-${index}`}
                      value={skill.skillName}
                      onChange={(value) => updateSkill(index, { skillName: value })}
                      options={skillOptions}
                      placeholder="Select skill"
                    />
                  </FormField>
                  <FormField label="Proficiency" htmlFor={`wizard-proficiency-${index}`}>
                    <Select
                      id={`wizard-proficiency-${index}`}
                      value={String(skill.proficiency || 1)}
                      onChange={(value) => updateSkill(index, { proficiency: Number(value) || 1 })}
                      options={PROFICIENCY_OPTIONS}
                    />
                  </FormField>
                  <button
                    type="button"
                    className="btn ghost sm resource-wizard-skill-remove"
                    onClick={() => removeSkill(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="btn secondary sm" onClick={addSkill}>
                Add skill
              </button>
            </fieldset>

            <fieldset className="resource-wizard-fieldset">
              <legend>Access tags</legend>
              {accessTagOptions.length === 0 ? (
                <p className="muted small">No access tags recorded on the roster.</p>
              ) : (
                <div className="resource-wizard-access-grid">
                  {accessTagOptions.map((tag) => (
                    <label key={tag} className="resource-wizard-access-option">
                      <input
                        type="checkbox"
                        checked={accessTags.includes(tag)}
                        onChange={() => toggleAccessTag(tag)}
                      />
                      <span>{tag}</span>
                    </label>
                  ))}
                </div>
              )}
            </fieldset>

            {errorMessage ? <ErrorState error={{ message: errorMessage }} /> : null}
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button type="submit" className="btn primary" disabled={findMut.running}>
                {findMut.running ? 'Finding candidates…' : 'Find candidates'}
              </button>
            </div>
          </form>
        ) : null}

        {step === 2 ? (
          <div className="resource-panel">
            <div className="row spread" style={{ gap: 12, flexWrap: 'wrap' }}>
              <h3>Candidates</h3>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                {recommendationMeta?.reviewState ? (
                  <span className="chip outline sm" title={recommendationMeta.reviewState}>
                    {humanizeEnum(recommendationMeta.reviewState)}
                  </span>
                ) : null}
                {recommendationMeta?.confidence != null ? (
                  <span className="chip blue sm">
                    Confidence {Number(recommendationMeta.confidence).toFixed(2)}
                  </span>
                ) : null}
                {recommendationMeta?.citationCount ? (
                  <span className="chip blue sm">{recommendationMeta.citationCount} citations</span>
                ) : null}
                {recommendationMeta?.runId ? (
                  <span className="chip outline sm">Run {recommendationMeta.runId}</span>
                ) : null}
              </div>
            </div>
            {recommendationMeta?.approvalRequired && recommendationMeta?.approvalSummary ? (
              <div className="resource-approval-banner">{recommendationMeta.approvalSummary}</div>
            ) : null}
            {recommendationCitations.length ? (
              <div className="resource-card-footer">
                {recommendationCitations.map((citation, index) => (
                  <CitationSourceLink
                    key={`${citation.source || citation.artifactId || index}`}
                    id={citation.source || citation.artifactId || ''}
                    className="chip outline sm"
                  />
                ))}
              </div>
            ) : null}
            {recommendations.length === 0 ? (
              <EmptyState
                title="No candidates"
                description="The governed run returned no recommendations for this demand — adjust the demand and retry."
              />
            ) : (
              <div className="resource-candidate-compare">
                {recommendations.map((recommendation) => {
                  const alreadyAssigned =
                    Boolean(allocationId) && recommendation.resourceId === currentResourceId;
                  const selectable = recommendation.fitStatus === 'qualified' && !alreadyAssigned;
                  return (
                    <div key={recommendation.id} className="resource-recommendation-shell">
                      <RecommendationCard recommendation={recommendation} />
                      <div className="row spread" style={{ gap: 12, marginTop: 12 }}>
                        <span className="muted small">
                          {alreadyAssigned
                            ? 'Currently assigned to this allocation'
                            : selectable
                              ? 'Eligible to assign'
                              : 'Constrained — cannot be selected'}
                        </span>
                        <button
                          type="button"
                          className="btn primary sm"
                          disabled={!selectable}
                          onClick={() => selectCandidate(recommendation)}
                        >
                          Select candidate
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="row" style={{ justifyContent: 'flex-start', marginTop: 12 }}>
              <button type="button" className="btn secondary sm" onClick={() => setStep(1)}>
                Back to demand
              </button>
            </div>
          </div>
        ) : null}

        {step === 3 && selectedCandidate ? (
          <div className="resource-panel resource-form">
            <div className="row spread" style={{ gap: 12 }}>
              <h3>Confirm assignment</h3>
              <span className="chip outline sm">Step 3 of 3</span>
            </div>
            <article className="resource-row-card">
              <div className="row spread" style={{ gap: 12 }}>
                <strong>{selectedCandidate.displayName || selectedCandidate.resourceId}</strong>
                <span className="chip blue sm">Score {selectedCandidate.score ?? 0}</span>
              </div>
              <p className="muted">
                {Number(hoursPerWeek || 0)}h/week · {loeId || 'Unassigned LOE'} ·{' '}
                {allocationId
                  ? `replaces the over-allocated assignment on ${allocationId}`
                  : activeRequest?.title || requestId || 'staffing demand'}
              </p>
            </article>
            <FormField
              label="Audit note"
              htmlFor="wizard-audit-note"
              hint="Recorded with the assignment decision for audit."
            >
              <Textarea id="wizard-audit-note" value={auditNote} onChange={setAuditNote} rows={3} />
            </FormField>
            {errorMessage ? <ErrorState error={{ message: errorMessage }} /> : null}
            {!canApproveAssignments ? (
              <p className="muted">
                Applying this assignment requires the Resource Manager role. Your selection and
                audit note are held here for a Resource Manager to approve.
              </p>
            ) : null}
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn secondary" onClick={() => setStep(2)}>
                Back to candidates
              </button>
              <button
                type="button"
                className="btn primary"
                disabled={!canApproveAssignments || applyMut.running}
                onClick={applyAssignment}
              >
                {!canApproveAssignments
                  ? 'Await Resource Manager approval'
                  : applyMut.running
                    ? 'Applying…'
                    : allocationId
                      ? 'Apply reassignment'
                      : 'Create allocation'}
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </>
  );
}

export function ProjectResourcesTab({ projectId }) {
  const resources = useApi(`/projects/${projectId}/resources`, { skip: !projectId });
  const requests = useApi('/resource-requests', { query: { projectId }, skip: !projectId });
  const requestMut = useMutation();
  const [requestTitle, setRequestTitle] = useState('');
  const [roleName, setRoleName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hoursPerWeek, setHoursPerWeek] = useState(null);
  const [requiredSkills, setRequiredSkills] = useState('');
  const [accessTags, setAccessTags] = useState('');
  const [loeId, setLoeId] = useState('');
  const [justification, setJustification] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [localRequests, setLocalRequests] = useState([]);

  if (resources.loading || requests.loading) return <LoadingSkeleton rows={5} />;
  if (resources.error) return <ErrorState error={resources.error} />;
  if (requests.error) return <ErrorState error={requests.error} />;

  const data = resources.data?.data || {};
  const allocations = data.allocations || [];
  const staffingGaps = data.staffingGaps || [];
  const requestRows = [...localRequests, ...(requests.data?.data || [])].filter(
    (request, index, rows) => rows.findIndex((candidate) => candidate.id === request.id) === index,
  );

  async function submitStaffingRequest(event) {
    event.preventDefault();
    setSubmitError('');
    setSubmitMessage('');
    try {
      const response = await requestMut.run(() =>
        api.post(`/projects/${projectId}/resource-requests`, {
          title: requestTitle,
          roleName,
          startDate,
          endDate,
          hoursPerWeek: Number(hoursPerWeek || 0),
          requiredSkills: parseRequiredSkills(requiredSkills),
          accessTags: parseAccessTags(accessTags),
          loeId,
          justification,
        }),
      );
      const createdRequest = response?.data?.data || response?.data || response;
      setLocalRequests((current) => [
        createdRequest,
        ...current.filter((item) => item.id !== createdRequest.id),
      ]);
      setSubmitMessage('Staffing request submitted. Queued for resource manager review.');
      setRequestTitle('');
      setRoleName('');
      setStartDate('');
      setEndDate('');
      setHoursPerWeek(null);
      setRequiredSkills('');
      setAccessTags('');
      setLoeId('');
      setJustification('');
    } catch (error) {
      setSubmitError(error.message || 'Staffing request failed.');
    }
  }

  return (
    <div className="profile-tab resource-project-tab">
      <section className="resource-shell-grid">
        <div className="resource-panel">
          <div className="row spread" style={{ gap: 12 }}>
            <h3>Assigned resources</h3>
            <Link to="/resources/scheduler">Open scheduler</Link>
          </div>
          {allocations.length === 0 ? (
            <EmptyState
              title="No project allocations"
              description="No planned resource allocations were found for this project."
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Resource</th>
                  <th>Role</th>
                  <th>LOE</th>
                  <th>Hours</th>
                  <th>Status</th>
                  <th>Conflict</th>
                </tr>
              </thead>
              <tbody>
                {allocations.map((allocation) => (
                  <tr key={allocation.id}>
                    <td>{allocation.displayName || allocation.resourceId}</td>
                    <td>{allocation.role || '—'}</td>
                    <td>{allocation.loeId || 'Unassigned'}</td>
                    <td>{allocation.hoursPerWeek || 0}h/week</td>
                    <td>
                      <StatusChip status={allocation.status} />
                    </td>
                    <td>
                      {allocation.conflict?.hasConflict ? (
                        <div className="resource-card-footer">
                          <span className="chip amber sm">
                            {allocation.conflict.varianceHours ?? 0}h over capacity
                          </span>
                          {allocation.conflictDetailRoute ? (
                            <Link to={allocation.conflictDetailRoute}>Open conflict detail</Link>
                          ) : null}
                          <Link to={`/resources/assignments/new?allocationId=${allocation.id}`}>
                            Resolve now
                          </Link>
                        </div>
                      ) : (
                        <span className="muted small">Clear</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="resource-panel">
          <div className="row spread" style={{ gap: 12 }}>
            <h3>Staffing gaps</h3>
            <Link to="/resources/assignments/new">Open assignment wizard</Link>
          </div>
          {staffingGaps.length === 0 ? (
            <EmptyState
              title="No staffing gaps"
              description="This project currently has no flagged capability shortages."
            />
          ) : (
            <div className="resource-stack">
              {staffingGaps.map((gap) => (
                <article key={`${gap.skill}-${gap.impact}`} className="resource-row-card">
                  <div className="row spread" style={{ gap: 12 }}>
                    <strong>{gap.skill}</strong>
                    <span className="chip amber sm">{gap.gapFte || 0} FTE gap</span>
                  </div>
                  <p className="muted">
                    Required {gap.requiredFte || 0} · assigned {gap.assignedFte || 0}
                  </p>
                  <p>{gap.impact || 'No impact narrative recorded.'}</p>
                </article>
              ))}
            </div>
          )}
        </div>

        <form className="resource-panel resource-form" onSubmit={submitStaffingRequest}>
          <div className="row spread" style={{ gap: 12 }}>
            <h3>Request staffing support</h3>
            <span className="chip outline sm">Project Manager action</span>
          </div>
          <FormField label="Request title" htmlFor="resource-request-title" required>
            <TextInput
              id="resource-request-title"
              value={requestTitle}
              onChange={setRequestTitle}
            />
          </FormField>
          <FormField label="Role support needed" htmlFor="resource-request-role" required>
            <TextInput id="resource-request-role" value={roleName} onChange={setRoleName} />
          </FormField>
          <FormField label="Start date" htmlFor="resource-request-start" required>
            <TextInput id="resource-request-start" value={startDate} onChange={setStartDate} />
          </FormField>
          <FormField label="End date" htmlFor="resource-request-end" required>
            <TextInput id="resource-request-end" value={endDate} onChange={setEndDate} />
          </FormField>
          <FormField label="Hours per week" htmlFor="resource-request-hours" required>
            <NumberInput
              id="resource-request-hours"
              value={hoursPerWeek}
              onChange={setHoursPerWeek}
              min={1}
              step={1}
            />
          </FormField>
          <FormField
            label="Required skills"
            htmlFor="resource-request-skills"
            hint="Example: Cloud Architect:4, ATO Readiness:3"
          >
            <TextInput
              id="resource-request-skills"
              value={requiredSkills}
              onChange={setRequiredSkills}
            />
          </FormField>
          <FormField
            label="Access tags"
            htmlFor="resource-request-access"
            hint="Comma-separated access tags"
          >
            <TextInput id="resource-request-access" value={accessTags} onChange={setAccessTags} />
          </FormField>
          <FormField label="LOE" htmlFor="resource-request-loe">
            <TextInput id="resource-request-loe" value={loeId} onChange={setLoeId} />
          </FormField>
          <FormField label="Justification" htmlFor="resource-request-justification">
            <TextInput
              id="resource-request-justification"
              value={justification}
              onChange={setJustification}
            />
          </FormField>
          {submitError ? <ErrorState error={{ message: submitError }} /> : null}
          {submitMessage ? <div className="resource-success-banner">{submitMessage}</div> : null}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="submit" className="btn primary" disabled={requestMut.running}>
              Submit staffing request
            </button>
          </div>
        </form>

        <div className="resource-panel">
          <div className="row spread" style={{ gap: 12 }}>
            <h3>Staffing requests</h3>
            <span className="chip outline sm">{requestRows.length}</span>
          </div>
          {requestRows.length === 0 ? (
            <EmptyState
              title="No staffing requests"
              description="Submitted project resource demand will queue here for review and assignment."
            />
          ) : (
            <div className="resource-stack">
              {requestRows.map((request) => (
                <article key={request.id} className="resource-row-card">
                  <div className="row spread" style={{ gap: 12 }}>
                    <strong>{request.title || request.roleName || request.id}</strong>
                    <StatusChip status={request.status || 'submitted'} />
                  </div>
                  <p className="muted">
                    {request.roleName || 'Role not specified'} · {request.startDate || 'Start TBD'}{' '}
                    to {request.endDate || 'End TBD'}
                  </p>
                  <p className="muted">
                    {request.hoursPerWeek || 0}h/week · {request.loeId || 'Unassigned LOE'}
                  </p>
                  {request.status === 'submitted' ? (
                    <p>Queued for resource manager review.</p>
                  ) : request.assignedResourceId ? (
                    <p>Assigned to {request.assignedResourceId}.</p>
                  ) : (
                    <p>{request.reviewDisposition || 'Request reviewed.'}</p>
                  )}
                  <div className="resource-card-footer">
                    <Link
                      to={`/resources/assignments/new?requestId=${encodeURIComponent(request.id)}`}
                    >
                      Open assignment wizard
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
